import {
    Alert,
    Badge,
    Button,
    Collapse,
    DatePicker,
    Divider,
    message,
    Modal,
    Space,
    Spin,
    Steps,
    Tooltip,
} from 'antd'
import { SyncOutlined, SolutionOutlined, LockFilled, UnlockFilled } from '@ant-design/icons'
import React, { useEffect, useState } from 'react'
import { ITaxYear, ITaxYearDataRes, OrderedreportId } from './types'
// import TaxYearStep from '../../components/TaxYearStep'
import AttachAdvisorModal from './AttachAdvisorModal'
import { APIFunctions } from '../../lib/apiFunctions'
import { User } from '../../types/session'
import OverviewConfigureReport from './OverviewConfigureReport'
import { APIFunctionTypes } from '../../lib/enums'
import AdvisorUserDisplay from '../Revisor/AdvisorUserDisplay'
import moment from 'moment'
import FileSaver from 'file-saver'
import ShareModal from './ShareModal'
import { formatText, layoutDictKeyFormat } from '../../lib/dateFunctions'

const { RangePicker } = DatePicker
const { Step } = Steps
const { Panel } = Collapse

enum progressSteps {
    SpecifyTaxYear = 0,
    LockReports = 1,
    LockTaxYear = 2,
    SendToSkat = 3,
    SkatApproves = 4,
}

export default function TaxYearOverview({
    taxYear,
    getTaxYearData,
    loadOverviewData,
    switchToTab,
    apiFunctions,
    userData,
    resetTaxYearData,
}: {
    taxYear: ITaxYear
    getTaxYearData: (taxYear: ITaxYear, forceFetch?: boolean) => Promise<ITaxYearDataRes>
    loadOverviewData: (postSwitchToTab?: string) => Promise<ITaxYear[]>
    switchToTab: (tab: string) => void
    apiFunctions: APIFunctions
    userData: User | null
    resetTaxYearData: (taxYear: ITaxYear) => void
}) {
    const [isLoading, setIsLoading] = useState<boolean>(!taxYear)
    const [shareModalVisible, setShareModalVisible] = useState<boolean>(false)
    const [isDownloading, setIsDownloading] = useState<boolean>(false)
    const [attachAdvisorModalVisible, setAttachAdvisorModalVisible] = useState<boolean>(false)
    const [currentProgress, setCurrentProgress] = useState<number>(1)
    const [currentReportsProgress, setCurrentReportsProgress] = useState<number>(
        progressSteps.SpecifyTaxYear
    )
    const [previousTaxYearId, setPreviousTaxYearId] = useState<string>(null)
    const [isFinishedLocking, setIsFinishedLocking] = useState<boolean>(false)
    const [intervalRef, setIntervalRef] = useState<any>(null)

    async function checkDownloadRdy(): Promise<boolean> {
        for (var rid of taxYear.report_ids) {
            if (!rid.is_locked) continue
            const isFinished = await apiFunctions.getLockedReportUploadFinished(
                rid.open_report_id,
                rid.locked_report_id
            )
            if (!isFinished) {
                setIsFinishedLocking(false)
                return false
            }
        }
        setIsFinishedLocking(true)
        if (intervalRef) {
            clearInterval(intervalRef)
        }
        return true
    }

    async function setupDownloadInterval() {
        const isReady = await checkDownloadRdy()
        if (isReady) return
        const interval = setInterval(checkDownloadRdy, 5000)
        setIntervalRef(interval)
    }

    useEffect(() => {
        if (previousTaxYearId !== taxYear.id) {
            setPreviousTaxYearId(taxYear.id)
            setIsFinishedLocking(false)
            if (intervalRef) clearInterval(intervalRef)
            setIntervalRef(null)
        } else if (intervalRef) {
            return
        }
        if (
            !(
                taxYear &&
                taxYear.report_ids.length > 0 &&
                taxYear.report_ids.filter((rid) => rid.is_locked).length > 0
            )
        ) {
            return
        }
        setupDownloadInterval()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [taxYear, apiFunctions])

    useEffect(() => {
        if (isFinishedLocking && intervalRef) {
            clearInterval(intervalRef)
        }
    }, [intervalRef, isFinishedLocking])

    useEffect(() => {
        if (taxYear.is_locked) {
            setCurrentProgress(progressSteps.SendToSkat)
        } else {
            const num_locked = taxYear.report_ids.filter((rid) => rid.is_locked).length
            const num_all = taxYear.report_ids.length
            if (num_all === num_locked && num_all > 0) {
                setCurrentProgress(progressSteps.LockTaxYear)
            } else {
                setCurrentProgress(progressSteps.LockReports)
                setCurrentReportsProgress(num_all > 0 ? 100 * (num_locked / num_all) : 0)
            }
        }
    }, [taxYear])

    async function updateTaxYear() {
        setIsLoading(true)
        await apiFunctions.refreshTaxYear(taxYear.id)
        await loadOverviewData()
        setIsLoading(false)
    }

    async function lockTaxYear() {
        setIsLoading(true)
        await apiFunctions.lockTaxYear(taxYear.id)
        await loadOverviewData()
        setIsLoading(false)
    }

    async function unlockTaxYear() {
        Modal.confirm({
            title: 'Are you sure?',
            content: `Do you want to unlock the tax year for ${taxYear.tax_year}?\nNormally you should never unlock a tax year once you have handed it in to SKAT.`,
            okText: 'Yes unlock tax year',
            cancelText: 'No',
            maskClosable: true,
            onOk: async () => {
                setIsLoading(true)
                await apiFunctions.unlockTaxYear(taxYear.id)
                await loadOverviewData()
                setIsLoading(false)
            },
        })
    }

    async function downloadTaxYear() {
        Modal.confirm({
            title: 'Download tax year',
            content: `You are about to download ALL data for tax year, ${taxYear.tax_year}.\nThis may take a couple of minutes.`,
            okText: 'Download',
            cancelText: 'Cancel',
            maskClosable: true,
            onOk: async () => {
                setIsDownloading(true)
                const res = await getTaxYearData(taxYear)
                if (!res) {
                    return
                }
                const data = res.tax_year_reports
                for (let tyd of data) {
                    if (!tyd.report_meta.report_url) {
                        setIsDownloading(false)
                        message.error('Could not download tax year!')
                        return
                    }
                    FileSaver.saveAs(
                        tyd.report_meta.report_url,
                        `compliacne-report-${formatText(
                            new Date(tyd.report_meta.date_start),
                            layoutDictKeyFormat
                        )}-${formatText(new Date(tyd.report_meta.date_end), layoutDictKeyFormat)}.zip`
                    )
                    await new Promise(r => setTimeout(r, 1000))
                }
                setIsDownloading(false)
            },
        })
    }

    function switchToReport(reportId: string) {
        switchToTab(`taxYearReport;;;${taxYear.id}:::${reportId}`)
    }

    const LoadingComp = <SyncOutlined style={{ fontSize: 100 }} spin />

    if (isLoading) {
        return (
            <Spin spinning={isLoading} indicator={LoadingComp} className="report-spinner">
                <div className="report-container" />
            </Spin>
        )
    }

    function renderAdvisor() {
        if (taxYear.advisor_id) {
            if (taxYear.advisor_accepted) {
                if (apiFunctions.APIType === APIFunctionTypes.LLUser) {
                    return (
                        <>
                            <AdvisorUserDisplay
                                loadOverviewData={loadOverviewData}
                                taxYear={taxYear}
                            />
                        </>
                    )
                } else if (apiFunctions.APIType === APIFunctionTypes.RevisorUser) {
                    return <></>
                }
            } else {
                if (apiFunctions.APIType === APIFunctionTypes.LLUser) {
                    return (
                        <>
                            <AdvisorUserDisplay
                                loadOverviewData={loadOverviewData}
                                taxYear={taxYear}
                            />
                        </>
                    )
                }
            }
        } else {
            return (
                <Button
                    icon={<SolutionOutlined />}
                    type="primary"
                    onClick={() => {
                        setAttachAdvisorModalVisible(true)
                    }}
                >
                    Request Advisor
                </Button>
            )
        }
    }

    return (
        <>
            {!taxYear.is_updated && (
                <Alert
                    message="Tax year is not up to date!"
                    description={
                        <>
                            Some changes to Exemption periods in the app is not included in this tax
                            year.
                            <br />
                            You can update the tax year, this will re-create the compliance reports
                            for this tax year.
                        </>
                    }
                    type="error"
                    showIcon
                    closeText={
                        <>
                            <Button
                                onClick={updateTaxYear}
                                type="primary"
                                style={{ marginRight: 20 }}
                            >
                                UPDATE
                            </Button>
                            <Button>IGNORE</Button>
                        </>
                    }
                />
            )}
            <div className="report-tab-header">
                <h1>
                    Complete Tax Year - {taxYear.tax_year} - {taxYear.tax_year_name}
                </h1>
                <Divider />
                {/* <Space>
                    <TaxYearStep size="large" text={'Specify Tax Year'} status="success" />
                    {(taxYear?.report_ids?.length === 1 && (
                        <div
                            onClick={() => {
                                switchToTab(
                                    `taxYearReport;;;${taxYear.id}:::${taxYear?.report_ids[0].id}`
                                )
                            }}
                        >
                            <TaxYearStep
                                size="large"
                                text={'Lock report'}
                                status={taxYear?.report_ids[0].is_locked ? 'success' : 'default'}
                                isBtn={true}
                            />
                        </div>
                    )) || (
                        <Space direction="vertical">
                            {taxYear?.report_ids?.map((rid) => {
                                return (
                                    <Space>
                                        <div
                                            onClick={() => {
                                                switchToTab(
                                                    `taxYearReport;;;${taxYear.id}:::${rid.id}`
                                                )
                                            }}
                                        >
                                            <TaxYearStep
                                                size="small"
                                                text={`Lock report ${rid.idx}`}
                                                status={rid.is_locked ? 'success' : 'default'}
                                                isBtn={true}
                                            />
                                        </div>
                                    </Space>
                                )
                            })}
                        </Space>
                    )}
                    <TaxYearStep size="large" text={'Send to SKAT'} />
                    <TaxYearStep size="large" text={'Approved'} />
                </Space>
                <br /> */}
                <Space direction="vertical" size="large">
                    <Steps
                        current={currentProgress}
                        percent={
                            currentProgress === progressSteps.LockReports
                                ? currentReportsProgress
                                : 0
                        }
                    >
                        <Step title="Specify Tax Year" />
                        <Step
                            title="Lock Reports"
                            description={`Status: ${
                                taxYear?.report_ids?.filter((rid) => rid.is_locked).length
                            } /${' '}
                    ${taxYear?.report_ids?.length}`}
                        />
                        <Step title="Lock Tax Year" />
                        <Step title="Send to SKAT" />
                        {/* <Step title="SKAT Approves" /> */}
                    </Steps>
                    <Collapse>
                        {taxYear?.report_ids?.map((rid: OrderedreportId) => {
                            return (
                                <Panel
                                    header={
                                        <div
                                            style={{
                                                display: 'flex',
                                                justifyContent: 'space-between',
                                            }}
                                        >
                                            {rid.is_locked ? (
                                                <Badge
                                                    text={
                                                        <span>
                                                            Report {rid.idx} -{' '}
                                                            <LockFilled
                                                                style={{ color: '#7EAC55' }}
                                                            />
                                                        </span>
                                                    }
                                                    status="success"
                                                />
                                            ) : (
                                                <Badge
                                                    text={
                                                        <span>
                                                            Report {rid.idx} -{' '}
                                                            <UnlockFilled
                                                                style={{ color: '#1890ff' }}
                                                            />{' '}
                                                            - Expand to configure
                                                        </span>
                                                    }
                                                    status="processing"
                                                />
                                            )}
                                            <RangePicker
                                                value={[
                                                    rid.date_start &&
                                                        moment(new Date(rid.date_start)),
                                                    rid.date_end && moment(new Date(rid.date_end)),
                                                ]}
                                                disabled={true}
                                            />
                                        </div>
                                    }
                                    key={rid.id}
                                >
                                    <div style={{ marginLeft: 20 }}>
                                        <OverviewConfigureReport
                                            userData={userData}
                                            apiFunctions={apiFunctions}
                                            reportIdInfo={rid}
                                            resetTaxYearData={() => {
                                                resetTaxYearData(taxYear)
                                            }}
                                            switchToReportTab={switchToReport}
                                        />
                                    </div>
                                </Panel>
                            )
                        })}
                    </Collapse>
                    {currentProgress === progressSteps.LockReports && (
                        <h4>
                            All reports must be locked before you can send or download the complete
                            tax year.
                        </h4>
                    )}
                    {currentProgress === progressSteps.LockTaxYear && (
                        <>
                            <h3>All reports are locked! You can now lock the whole tax year.</h3>
                            <Button type="primary" onClick={lockTaxYear}>
                                Lock Tax Year
                            </Button>
                        </>
                    )}
                    {currentProgress === progressSteps.SendToSkat && (
                        <>
                            <h3>You are now ready to hand in your reports for this tax year.</h3>
                            <p>
                                <span style={{ fontWeight: 'bold' }}>
                                    To hand in your tax year, you have 2 options:
                                </span>
                                <br />
                                - Share a link to ll33a.dk with access to your data
                                <br />- Download your tax year and send it manually to the
                                authorities. To do this you can use{' '}
                                <a
                                    target="_blank"
                                    rel="noreferrer"
                                    style={{ textDecoration: 'underline' }}
                                    href="https://cloud.bluewhale.dk/startPortal/pages/index.jsp"
                                >
                                    Bluewhale.
                                </a>
                            </p>
                            <Space direction="horizontal" size="large">
                                <Tooltip
                                    title={
                                        isFinishedLocking ? 'Share Tax Year' : 'Preparing Tax Year'
                                    }
                                >
                                    <Button
                                        type="primary"
                                        onClick={()=>{setShareModalVisible(true)}}
                                        loading={shareModalVisible || !isFinishedLocking}
                                        disabled={!isFinishedLocking}
                                    >
                                        Share Tax Year through ll33a.dk
                                    </Button>
                                </Tooltip>
                                <Tooltip
                                    title={
                                        isFinishedLocking
                                            ? 'Download Tax Year'
                                            : 'Preparing download'
                                    }
                                >
                                    <Button
                                        type="primary"
                                        onClick={downloadTaxYear}
                                        loading={isDownloading || !isFinishedLocking}
                                        disabled={!isFinishedLocking}
                                    >
                                        Download Tax Year
                                    </Button>
                                </Tooltip>
                                <Button onClick={unlockTaxYear}>Unlock Tax Year</Button>
                            </Space>
                        </>
                    )}
                    <Divider />
                    {renderAdvisor()}
                    <AttachAdvisorModal
                        loadOverviewData={loadOverviewData}
                        taxYear={taxYear}
                        visible={attachAdvisorModalVisible}
                        setVisible={setAttachAdvisorModalVisible}
                    />
                </Space>
            </div>
            <ShareModal
                apiFunctions={apiFunctions}
                taxYear={taxYear}
                visible={shareModalVisible}
                setVisible={setShareModalVisible}
            />
        </>
    )
}
